import React from "react";
import PropTypes from "prop-types";
import FormInput from "Common/Custom/FormInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormSelect from "Common/Custom/FormSelect";
import Switch from "Common/Custom/Switch";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import { getSelectOptions } from "utils/optionHelper";
import SearchRoom from "../../SearchRoom/SearchRoom";

/**
 * AddHousekeepingForm
 * Pure presentational component — no API calls, no hooks.
 * Receives all data and callbacks via props from AddHousekeeping.
 */
const AddHousekeepingForm = ({
    fields = [],
    values = {},
    errors = {},
    handleChange = () => { },
    handleSubmit = () => { },
    loading = false,
    managers = [],
    cleaners = [],
    setValues,
}) => {
    // Dropdown option config — pre-filtered arrays from the backend
    const optionConfig = {
        assigned_by: { list: managers, valueKey: "employee_id", labelKey: "full_name" },
        assigned_to: { list: cleaners, valueKey: "employee_id", labelKey: "full_name" },
    };

    const renderField = (field) => {
        if (field.type === "select") {
            return (
                <FormSelect
                    key={field.name}
                    field={field}
                    value={values[field.name] ?? ""}
                    handleChange={handleChange}
                    options={getSelectOptions(field.name, optionConfig)}
                    error={errors[field.name]}
                />
            );
        }

        if (field.type === "switch") {
            return (
                <Switch
                    key={field.name}
                    field={field}
                    values={values[field.name]}
                    handleChange={handleChange}
                    error={errors[field.name]}
                    ValueOne="1"
                    ValueTwo="0"
                    LabelOne="Yes"
                    LabelTwo="No"
                />
            );
        }

        if (field.type === "date") {
            return (
                <FormDatePicker
                    key={field.name}
                    field={field}
                    value={values[field.name] ?? ""}
                    handleChange={handleChange}
                    error={errors[field.name]}
                    mode="datetime"
                />
            );
        }

        return (
            <FormInput
                key={field.name}
                field={field}
                value={values[field.name] ?? ""}
                handleChange={handleChange}
                error={errors[field.name]}
            />
        );
    };

    return (
        <PageBody
            headerchildren={<HeadingOne>Create Housekeeping</HeadingOne>}
            onSubmit={handleSubmit}
        >
            {/* Room search + selection panel */}
            <SearchRoom
                errors={errors}
                values={values}
                handleChange={handleChange}
                loading={loading}
                setValues={setValues}
            />

            <div className="col-12 mt-4"><hr /></div>

            <div className="col-12 mb-3">
                <h6 className="mb-1">Housekeeping Details</h6>
                <small className="text-muted">Assign staff and schedule the cleaning window</small>
            </div>

            {fields.map(renderField)}

            <FormSubmitButton
                label="Create"
                loading={loading}
                colSize="col-12"
                align="end"
            />
        </PageBody>
    );
};

AddHousekeepingForm.propTypes = {
    fields: PropTypes.array,
    values: PropTypes.object,
    errors: PropTypes.object,
    handleChange: PropTypes.func,
    handleSubmit: PropTypes.func,
    loading: PropTypes.bool,
    managers: PropTypes.array,
    cleaners: PropTypes.array,
    setValues: PropTypes.func,
};

AddHousekeepingForm.defaultProps = {
    fields: [],
    values: {},
    errors: {},
    handleChange: () => { },
    handleSubmit: () => { },
    loading: false,
    managers: [],
    cleaners: [],
};

export default AddHousekeepingForm;
